<template>
  <div>
    <Spinner v-if="isLoading" class="flex" />
    <div v-else class="content">
      <div class="sec-wrapper" :class="disableFooter ? '' : 'padding-bottom-1'">
        <div class="icon-wrapper p-d-flex p-ai-center">
          <div style="color: var(--primary-color)">
            <SvgIcon name="loan_summary_icon" size="1.75rem" />
          </div>
          <p class="sec-title pm-txt-color gr-bold">Loan Summary</p>
        </div>
        <div class="section-1">
          <div class="grid-items">
            <div class="wrap">
              <p class="heading lt-txt-color">Loan</p>
              <p class="text pm-txt-color">
                {{ selectedLoanDetails.loanReferenceNumber }}
              </p>
            </div>
            <div v-if="selectedLoanDetails.calculatedValue?.interestFreeTermMonths > 0" class="wrap">
              <p class="heading lt-txt-color">Interest free period</p>
              <p class="text pm-txt-color">{{ selectedLoanDetails.calculatedValue?.interestFreeTermMonths }} months</p>
            </div>
            <!-- Holds the column so the fields after it keep their positions when there is no
                 interest free period, which is the norm for ERHL. -->
            <div v-else class="wrap"></div>
            <div class="wrap">
              <p class="heading lt-txt-color">Valuation Fee</p>
              <p class="text pm-txt-color">
                {{ formatCurrency(selectedLoanDetails.calculatedValue.valuationFee) }}
              </p>
            </div>
            <div class="wrap">
              <p class="heading lt-txt-color">{{ setupFeeLabel }}</p>
              <p class="text pm-txt-color">
                {{
                  hideTotalSetupFee(selectedLoanDetails.crmStatus) ? 'TBD' : `${(setupFeePercentage * 100).toFixed(2)}%`
                }}
              </p>
            </div>
            <div class="wrap">
              <p class="heading lt-txt-color">Term</p>
              <p class="text pm-txt-color">{{ loanTermDisplay }}</p>
            </div>
            <div class="wrap">
              <template v-if="isPreSettlement && selectedLoanDetails.calculatedValue?.interestFreeTermMonths > 0">
                <p class="heading lt-txt-color">Interest rate</p>
                <p class="text">{{ (interestRate * 100).toFixed(2) }}% after interest free period</p>
              </template>
              <template v-else-if="isPreSettlement">
                <p class="heading lt-txt-color">Interest rate</p>
                <p class="text">{{ (interestRate * 100).toFixed(2) }}%</p>
              </template>
            </div>
            <div class="wrap">
              <p class="heading lt-txt-color">Loan Deposit</p>
              <p class="text pm-txt-color">
                {{ formatCurrency(selectedLoanDetails.calculatedValue.depositAmount) }}
              </p>
            </div>
            <div class="wrap">
              <p class="heading lt-txt-color">
                {{ hideTotalSetupFee(selectedLoanDetails.crmStatus) ? 'Estimated Loan Amount' : 'Loan Amount' }}
              </p>
              <p class="text primary-color">
                {{ formatCurrency(selectedLoanDetails.calculatedValue.loanAmount) }}
              </p>
            </div>
            <template v-if="isErhl">
              <div class="wrap">
                <p class="heading lt-txt-color">Loan Type</p>
                <p class="text pm-txt-color">{{ loanTypeLabel }}</p>
              </div>
              <div class="wrap">
                <p class="heading lt-txt-color">Scheduled equity release term</p>
                <p class="text pm-txt-color">
                  {{ formatErhlYears(equityReleaseTermYears) }}
                </p>
              </div>
              <div class="wrap">
                <p class="heading lt-txt-color">Scheduled equity release amount</p>
                <p class="text pm-txt-color">
                  {{ formatNAOrCurrency(scheduledEquityRelease && scheduledEquityRelease.amount) }}
                </p>
              </div>
              <div class="wrap">
                <p class="heading lt-txt-color">{{ lumpSumLabel }}</p>
                <p class="text pm-txt-color">
                  {{ formatNAOrCurrency(lumpSumDrawdown && lumpSumDrawdown.amount) }}
                </p>
              </div>
            </template>
          </div>
        </div>
        <div class="section-2">
          <div class="title-icon p-d-flex p-ai-center">
            <div style="color: var(--primary-color)">
              <SvgIcon name="supporting_document" size="1.75rem" />
            </div>
            <p class="sec-title pm-txt-color">Supporting Documents</p>
          </div>
          <div class="p-d-flex p-ai-center table">
            <div class="sec-content">
              <DataTable :value="displaySupportingDocuments" responsiveLayout="scroll">
                <Column field="name" header="Document">
                  <template #body="slotProps">
                    <div class="status-text">
                      {{ slotProps.data.name }}
                    </div>
                  </template>
                </Column>
                <Column field="dueDate" header="Due Date" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <div class="status-text">
                      {{ new Date(slotProps.data.dueDate).toLocaleDateString() }}
                    </div>
                  </template>
                </Column>
                <Column field="status" header="Status" :style="{ width: '160px' }">
                  <template #body="{ data }">
                    <div
                      class="status-text"
                      :class="[data.isReady && data.assignedToCurrentUser ? 'color-highlight' : 'lt-txt-color']"
                      @click="data.isReady && data.assignedToCurrentUser && openFileUploadModal(data)"
                    >
                      {{ data.statusDescription }}
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>
        <div class="section-3">
          <div class="title-icon p-d-flex p-ai-center">
            <div style="color: var(--primary-color)">
              <SvgIcon name="supporting_document" size="1.75rem" />
            </div>
            <p class="sec-title pm-txt-color">Special Conditions</p>
          </div>
          <div class="p-d-flex p-ai-center table">
            <div class="sec-content">
              <DataTable :value="specialConditions" responsiveLayout="scroll">
                <Column field="condition" header="Condition" />
                <Column field="dueDate" header="Due Date" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <div class="status-text">
                      {{
                        slotProps.data.dueDate
                          ? new Date(slotProps.data.dueDate).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : ''
                      }}
                    </div>
                  </template>
                </Column>
                <Column field="statusDescription" header="Status" :style="{ width: '150px' }">
                  <template #body="{ data }">
                    <div class="status-text">
                      {{ data.statusDescription }}
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>

        <div class="section">
          <Properties
            :items="selectedLoanDetails.incomingProperties"
            :settlementDate="selectedLoanDetails.settlementDate"
            propertyKind="incoming"
            title="Purchase Properties"
          />
          <Properties
            :items="selectedLoanDetails.outgoingProperties"
            propertyKind="outgoing"
            class="mt-5"
            title="Properties for sale"
          />
          <Properties
            v-if="hasSupportingProperties"
            :items="selectedLoanDetails.supportingProperties"
            propertyKind="supporting"
            class="mt-5"
            title="Supporting Properties"
          />
        </div>
      </div>
      <FileUpload
        :key="uploadKey"
        :display="displayFileuploadDialog"
        :taskId="selectedTask.id ?? ''"
        :taskTitle="selectedTask.title ?? ''"
        :status="selectedTask.status ?? ''"
        :externalDescription="selectedTask.externalDescription ?? ''"
        :documents="selectedTask.supportingDocuments ?? []"
        @completed="fileUploadCompleted"
        @skipped="closeBasic"
      />
    </div>
    <ConditionalOfferDialog :display="displayConditonalDialog" @closeCondition="closeCondition" :key="conditionKey" />
  </div>
</template>

<script>
import {
  LUMP_SUM,
  PURCHASE_NEW_PROPERTY,
  SCHEDULED_EQUITY_RELEASE,
  findDrawdown,
  formatNAOrCurrency,
} from '@/services/erhlSummary';
import { SPECIAL_CONDITION_STATUS, SUPPORTING_DOCUMENT_STATUS } from '@/constants';
import {
  formatCurrency,
  formatErhlValue,
  formatLoanTermDisplay,
  fromSnakeCase,
  monthsToYears,
  yearsFormatter,
} from '@/services/format';
import { getSetupFeeRate } from '@/services/setupFee';
import { isStatusBefore } from '@/services/statusService';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import Column from 'primevue/column';
import ConditionalOfferDialog from '../Progress/ConditionalOfferDialog.vue';
import DataTable from 'primevue/datatable';
import FileUpload from '../Progress/FileUpload.vue';
import Properties from './Properties.vue';
import Screensize from '@/mixins/screensize.mixin';
import SvgIcon from '../../../../../components/common/SvgIcon.vue';

export default {
  components: {
    DataTable,
    Column,
    Properties,
    FileUpload,
    ConditionalOfferDialog,
    SvgIcon,
  },
  mixins: [Screensize],
  props: ['cmp', 'progressStage'],
  setup() {
    return {
      formatCurrency,
      fromSnakeCase,
      formatNAOrCurrency,
    };
  },
  data() {
    return {
      displayConditonalDialog: false,
      conditionKey: 0,
      uploadKey: 0,
      isLoading: true,
      displayFileuploadDialog: false,
      disableFooter: true,
      selectedTask: {
        id: '',
        title: '',
        status: '',
        externalDescription: '',
        supportingDocuments: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      selectedLoanDetails: 'loans/selectedLoanDetails',
      selectedLoanId: 'loans/selectedLoanId',
      tasksList: 'loans/tasksList',
      selectedLoanStatus: 'loans/selectedLoanStatus',
      supportingDocuments: 'loans/supportingDocuments',
      isErhl: 'loans/isErhl',
    }),
    isPreSettlement() {
      return !['loan_funded', 'loan_repaid'].includes(this.selectedLoanDetails.applicationStatus);
    },
    loanTermDisplay() {
      return formatLoanTermDisplay(this.selectedLoanDetails.calculatedValue?.loanTermMonths, this.isErhl);
    },
    setupFeeLabel() {
      return this.isErhl ? 'Set-up fee (%)' : 'Total set-up fee (%)';
    },
    setupFeePercentage() {
      return getSetupFeeRate(this.selectedLoanDetails.calculatedValue, this.isErhl);
    },
    hasSupportingProperties() {
      return (this.selectedLoanDetails.supportingProperties ?? []).length > 0;
    },
    displaySupportingDocuments() {
      return (this.supportingDocuments ?? []).map((task) => {
        const isReady = task.isInRange && task.status === 'not_yet_started';
        let status = !isReady && task.status === 'not_yet_started' ? 'not_yet_submitted' : task.status;
        if (!task.assignedToApplicantId && isReady) {
          status = 'not_assigned_to_user';
        }
        const statusDescription = SUPPORTING_DOCUMENT_STATUS[status] ?? fromSnakeCase(task.status);
        return {
          taskId: task.id,
          name: fromSnakeCase(task?.taskTitle) || 'Supporting document',
          status: task.status,
          assignedToCurrentUser: task.assignedToApplicantId,
          isReady,
          statusDescription,
          dueDate: task.dueDate,
          externalDescription: task.externalDescription,
          documents: task.documents,
        };
      });
    },
    specialConditions() {
      return this.selectedLoanDetails.specialConditions.map((sc) => {
        return {
          condition: sc.task?.externalDescription ?? '-',
          status: sc.specialConditionStatus,
          statusDescription:
            SPECIAL_CONDITION_STATUS[sc.specialConditionStatus] ?? fromSnakeCase(sc.specialConditionStatus),
          dueDate: sc.task?.dueDate,
        };
      });
    },
    interestRate() {
      const introRate = this.selectedLoanDetails.assessmentRates.find(
        (rate) => rate.assessmentRateType === 'introductory_variable_interest_rate',
      );
      if (introRate) {
        return introRate?.assessmentRate;
      } else {
        const bridgingRate = this.selectedLoanDetails.assessmentRates.find(
          (rate) => rate.assessmentRateType === 'bridging_variable_interest_rate',
        );
        return bridgingRate?.assessmentRate;
      }
    },
    isErhl() {
      return this.selectedLoanDetails.lendingProduct === 'erhl';
    },
    isPurchaseNewProperty() {
      const loanPurpose = this.selectedLoanDetails.loanPurpose;
      const erhlLoanPurposes = this.selectedLoanDetails.erhlDetails?.loanPurposes ?? [];

      return loanPurpose === PURCHASE_NEW_PROPERTY || erhlLoanPurposes.includes(PURCHASE_NEW_PROPERTY);
    },
    scheduledEquityRelease() {
      return findDrawdown(this.selectedLoanDetails.erhlDetails?.drawdowns, SCHEDULED_EQUITY_RELEASE);
    },
    equityReleaseTermYears() {
      return monthsToYears(this.scheduledEquityRelease?.durationTotalMonths);
    },
    lumpSumDrawdown() {
      return findDrawdown(this.selectedLoanDetails.erhlDetails?.drawdowns, LUMP_SUM);
    },
    // Same drawdown either way: on a purchase it funds the property at settlement, so it reads as
    // the initial loan amount.
    lumpSumLabel() {
      return this.isPurchaseNewProperty ? 'Initial loan amount' : 'Lump Sum';
    },
    loanTypeLabel() {
      if (this.isErhl) {
        return 'Equity Release Home Loan';
      }

      return fromSnakeCase(this.selectedLoanDetails.loanType);
    },
  },
  methods: {
    ...mapActions({
      fetchLoanSummary: 'loans/fetchLoanSummary',
      fetchLoanProgress: 'loans/fetchLoanProgress',
      fetchSupportingDocuments: 'loans/fetchSupportingDocuments',
    }),
    ...mapMutations({
      setSupportingDocuments: 'loans/setSupportingDocuments',
    }),
    toggle(event) {
      this.$refs.menu.toggle(event);
    },
    toggleCmp() {
      this.$emit('toggleCmp');
    },
    openFileUploadModal(data) {
      this.uploadKey += 1;
      Object.assign(this.selectedTask, {
        id: data.taskId,
        title: data.name,
        status: data.status,
        externalDescription: data.externalDescription,
        supportingDocuments: data.documents,
      });
      this.displayFileuploadDialog = true;
    },
    openBasic() {
      this.displayFileuploadDialog = true;
    },
    closeBasic() {
      this.displayFileuploadDialog = false;
    },
    hideTotalSetupFee(status) {
      return isStatusBefore(status, 'conditional_offer');
    },
    formatErhlYears(value) {
      return formatErhlValue(value, yearsFormatter);
    },
    async fileUploadCompleted() {
      this.displayFileuploadDialog = false;
      await this.fetchLoanSummary();
      // update task status
      this.setSupportingDocuments(
        this.supportingDocuments.map((doc) => {
          if (doc.id === this.selectedTask.id) {
            return { ...doc, status: 'in_progress' };
          }
          return doc;
        }),
      );
    },
    async acceptConditionalOffer() {
      await this.fetchLoanProgress();
      if (this.selectedLoanProgress?.tasks) {
        const list = this.selectedLoanProgress.tasks;
        const task = list.find((obj) => obj.modal_field == 'conditional');
        if (task) {
          this.disableFooter = false;
        } else {
          this.disableFooter = true;
        }
      } else {
        this.disableFooter = true;
      }
    },
    async closeCondition() {
      this.displayConditonalDialog = false;
      await this.acceptConditionalOffer();
    },
  },
  async created() {
    this.isLoading = false;
    try {
      await Promise.all[(this.fetchLoanSummary(), this.fetchSupportingDocuments())];
    } finally {
      this.isLoading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.content {
  margin-top: 2rem;
}

.padding-bottom-1 {
  padding-bottom: 1rem;
}

.sec-wrapper {
  border-bottom: 1px solid $light-text-color;
}

.footer {
  display: flex;
  justify-content: flex-end;
  padding: 2rem 1rem;
  @media (max-width: 992px) {
    padding: 0;
    justify-content: space-between;
  }
}

.title {
  font-weight: bold;
  color: $black-color;
  font-size: 2.1rem;
}

.desc {
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 2;
}

.text {
  margin-bottom: 0.3rem;
}

.field-not {
  width: 100%;
}

.sec-title {
  font-size: 1.25rem;
  margin: 0;
  font-family: $font-family !important;
  font-weight: 500;
}

.second-row {
  margin-top: 1rem;
}

.heading,
.text {
  font-size: 0.875rem !important;
  font-family: $font-family !important;
  font-weight: 500;
}

.inputs {
  gap: 1rem;
  align-items: flex-end;
}

.section-1 {
  margin-top: 2rem;
  border-bottom: 1px solid $light-text-color;
  padding-bottom: 1rem;

  .details-content {
    gap: 1.375rem;
  }

  .grid-items {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    padding: 0 1rem;
    width: 90%;
    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
      width: 100%;
    }
  }
}

.section-2 {
  margin-top: 1.875rem;

  .table {
    width: 100%;
  }

  .title-icon {
    padding: 0 1rem;
    gap: 1rem;
  }
}

.sec-content {
  padding: 0 1rem;
  width: 100%;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  color: $light-text-color;
  background-color: #fff;
  font-size: 1rem;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  text-align: left;
  border-bottom: 1px solid $light-text-color;
  color: $primary-text-color;
  font-size: 0.875rem;
}

.supporting-container {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
}

.section-3 {
  margin-top: 1.875rem;
  width: 100%;
}

.icon-wrapper {
  gap: 1rem;
  padding: 0 1rem;

  .icon {
    font-size: 2rem;
  }
}

.heading {
  margin: 0;
  // font-weight: bold;
  font-size: 1.25rem;
}
.color-highlight {
  color: $primary-color !important;
  // text-decoration: underline !important;
  cursor: pointer;
}
.back {
  color: rgba(2, 0, 56, 0.4);
}
.status-text {
  text-align: left;
  color: $primary-text-color;
  font-size: 0.875rem;
  text-decoration: none;
}
</style>
